import GetEnvironment from "../environments"
import { getSettings } from "./QuadriSettingsApi"

export type Header = {
  taskId: string
  name: string
  description: string
  taskTypeTag: string
  taskClass: string
  hasChildren: boolean
  creationDate?: string | null
  lastModifiedDate?: string | null
}

export type TaskTypeDefinition = {
  id: string
  name: string
  description: string
  iconSvg: string
}

export type Details = {
  type: TaskTypeDefinition
  properties: TaskTypeProperty[]
}

export type TaskType = {
  header: Header
  details: Details
  children: TaskType[]
  subTasks: TaskTypeSimple[]
}

export type TaskTypeSimple = {
  taskId: string
  name: string
  description: string
  taskTypeTag: string
  taskClass: string
  creationDate: string
  hasChildren: boolean
}

export type TaskTypePropertyDefinition = {
  name: string
  caption: string
  description: string
  type: string
  isBrowsable: boolean
  includeInTemplate: boolean
}

export type TaskTypeProperty = {
  definition: TaskTypePropertyDefinition
  value: any
}

export class QuadriRestApi {
  private baseUrl: string

  constructor() {
    this.baseUrl = ""
  }

  async initialize() {
    const accessToken = localStorage.getItem("tc-access-token")
    if (!accessToken) {
      throw new Error("Access token is missing from localStorage.")
    }
    const region = GetEnvironment().region
    if (!region) {
      throw new Error("Region is missing")
    }
    const settings = await getSettings(accessToken, region)
    if (settings) {
      this.baseUrl = settings.quadriApi?.uri
    } else {
      throw new Error("Settings could not be retrieved.")
    }

    console.log("Using quadri rest api url: ", this.baseUrl)
  }

  private requestInit(method?: string) {
    const accessToken = localStorage.getItem("tc-access-token")
    return { method: method || "GET", headers: { Authorization: "Bearer " + accessToken } }
  }

  public static encodeTaskId(projectId: string, taskId: string): string {
    // Regular expression to check if taskId is already a 8-character hexadecimal string
    const hexRegex = new RegExp(`^${projectId}-[0-9a-fA-F]{8}$`)
    if (hexRegex.test(taskId)) return taskId

    const taskIdNumber = parseInt(taskId, 10)
    const taskIdHex = taskIdNumber.toString(16)
    const paddedTaskIdHex = taskIdHex.padStart(8, "0")
    return `${projectId}-${paddedTaskIdHex}`
  }

  public async getTask(projectId: string, taskId: string): Promise<TaskType> {
    const init = this.requestInit()

    const taskType = await fetch(`${this.baseUrl}/api/v1/projects/${projectId}/tasks/${taskId}`, init).then((res) =>
      res.json()
    )
    return taskType
  }

  public async getTaskSchemas(): Promise<TaskTypeDefinition[]> {
    const init = this.requestInit()
    let allItems: TaskTypeDefinition[] = []
    let url = `${this.baseUrl}/api/v1/taskSchemas`

    while (url) {
      const response = await fetch(url, init)
      if (!response.ok) throw new Error(`Failed to fetch task schemas: ${response.statusText}`)

      const data = await response.json()
      if (Array.isArray(data.items)) allItems = allItems.concat(data.items)

      url = data.links?.next?.href ? `${this.baseUrl}${data.links.next.href}` : ""
    }
    return allItems
  }
}
